<script setup>
import { ref, onMounted, computed } from 'vue'
import Card from 'primevue/card'
import Button from 'primevue/button'
import { useRouter } from 'vue-router'
import InputText from 'primevue/inputtext'
import Dropdown from 'primevue/dropdown'
import { useAppToast } from '@/shared/lib/useAppToast'
import { usePagination } from '@/shared/composables/usePagination'

import ProductTypeFormDialog from '@/modules/admin/products/product-types/components/ProductTypeFormDialog.vue'
import DeleteProductTypeConfirmDialog from '@/modules/admin/products/product-types/components/DeleteProductTypeConfirmDialog.vue'

import {
  getAllProductTypes,
  getProductTypeById,
  createProductType,
  updateProductType,
  deleteProductType
} from '@/modules/admin/products/product-types/api/productTypes.service'

const router = useRouter()
const toast = useAppToast()

const productTypesList = ref([])
const productTypeFormMode = ref('create')
const selectedProductType = ref(null)

const productTypesDialogVisible = ref(false)
const deleteProductTypeDialogVisible = ref(false)

const loading = ref(false)
const submitting = ref(false)
const deleting = ref(false)

const fieldErrors = ref({})

const search = ref('')
const selectedBrandId = ref(null)
const selectedCategoryId = ref(null)

async function loadProductTypes() {
  try {
    loading.value = true

    const data = await getAllProductTypes()

    productTypesList.value = data.map((t) => ({
      id: t.Id ?? t.id,
      name: t.Name ?? t.name,
      collectionName: t.CollectionName ?? t.collectionName,
      modelCode: t.ModelCode ?? t.modelCode,
      description: t.Description ?? t.description,
      brandId: t.BrandId ?? t.brandId,
      categoryId: t.CategoryId ?? t.categoryId,
      isActive: t.IsActive ?? t.isActive,
      attributes: t.ProductTypeAttributes ?? t.attributes ?? []
    }))
  } catch (error) {
    productTypesList.value = []
    toast.error(error.message || 'Ошибка загрузки типов продуктов')
  } finally {
    loading.value = false
  }
}

function openCreateProductType() {
  selectedProductType.value = null
  productTypeFormMode.value = 'create'
  fieldErrors.value = {}
  productTypesDialogVisible.value = true
}

async function editProductType(type) {
  try {
    const fullType = await getProductTypeById(type.id)

    selectedProductType.value = {
      id: fullType.id ?? fullType.Id,
      name: fullType.name ?? fullType.Name,
      collectionName: fullType.collectionName ?? fullType.CollectionName,
      modelCode: fullType.modelCode ?? fullType.ModelCode,
      description: fullType.description ?? fullType.Description,
      brandId: fullType.brandId ?? fullType.BrandId,
      categoryId: fullType.categoryId ?? fullType.CategoryId,
      isActive: fullType.isActive ?? fullType.IsActive,
      attributes:
        (fullType.productTypeAttributes ?? fullType.attributes ?? []).map((a) => {
          const attr = a.productAttribute ?? a
          return { id: attr.id ?? attr.Id, name: attr.name ?? attr.Name, scope: a.scope ?? a.Scope ?? 0, selectedValueId: a.selectedValueId ?? a.SelectedValueId ?? null, values: attr.values ?? attr.Values ?? [] }
        })
    }

    productTypeFormMode.value = 'edit'
    fieldErrors.value = {}
    productTypesDialogVisible.value = true
  } catch (error) {
    toast.error(error.message || 'Ошибка при загрузке типа продукта')
  }
}

function viewProductType(type) {
  router.push(`/admin/product-types/${type.id}`)
}

function deleteProductTypeConfirm(type) {
  selectedProductType.value = type
  deleteProductTypeDialogVisible.value = true
}


async function handleDeleteProductType() {
  if (deleting.value) return
  try {
    if (!selectedProductType.value?.id) return

    deleting.value = true

    await deleteProductType(selectedProductType.value.id)

    toast.success('Тип продукта удалён')

    await loadProductTypes()
  } catch (error) {
    toast.error(error.message || 'Ошибка при удалении типа продукта')
  } finally {
    deleting.value = false
    deleteProductTypeDialogVisible.value = false
  }
}

async function handleSubmitProductType(formData) {
  try {
    submitting.value = true
    fieldErrors.value = {}

    const payload = {
      Id: selectedProductType.value?.id,
      Name: formData.name,
      CollectionName: formData.collectionName,
      ModelCode: formData.modelCode,
      Description: formData.description,
      BrandId: formData.brandId,
      CategoryId: formData.categoryId,
      IsActive: formData.isActive,
      AttributeIds: [...(formData.attributeIds ?? [])],
      AttributeDefinitions: formData.attributeDefinitions ?? null
    }

    if (productTypeFormMode.value === 'create') {
      await createProductType(payload)
      toast.success('Тип продукта создан')
    } else {
      await updateProductType(payload)
      toast.success('Тип продукта обновлён')
    }

    productTypesDialogVisible.value = false
    await loadProductTypes()
  } catch (error) {
    fieldErrors.value = error.fieldErrors || {}
    toast.error(error.message || 'Ошибка при сохранении типа продукта')
  } finally {
    submitting.value = false
  }
}

const brandOptions = computed(() => {
  const map = new Map()

  productTypesList.value.forEach(t => {
    if (t.brandId) {
      map.set(t.brandId, t.brandName ?? `Бренд ${t.brandId}`)
    }
  })

  return [
    { label: 'Все бренды', value: null },
    ...Array.from(map.entries()).map(([id, name]) => ({
      label: name,
      value: id
    }))
  ]
})

const categoryOptions = computed(() => {
  const map = new Map()

  productTypesList.value.forEach(t => {
    if (t.categoryId) {
      map.set(t.categoryId, t.categoryName ?? `Категория ${t.categoryId}`)
    }
  })

  return [
    { label: 'Все категории', value: null },
    ...Array.from(map.entries()).map(([id, name]) => ({
      label: name,
      value: id
    }))
  ]
})

const filteredProductTypes = computed(() => {
  return productTypesList.value.filter(type => {
    const searchText = search.value.toLowerCase()

    const matchesSearch =
      !searchText ||
      type.name?.toLowerCase().includes(searchText)

    const matchesBrand =
      !selectedBrandId.value ||
      type.brandId === selectedBrandId.value

    const matchesCategory =
      !selectedCategoryId.value ||
      type.categoryId === selectedCategoryId.value

    return matchesSearch && matchesBrand && matchesCategory
  })
})

function goBack() {
  router.push('/admin')
}

onMounted(() => {
  loadProductTypes()
})

const { currentPage, totalPages, paginatedItems: paginatedProductTypes } = usePagination(filteredProductTypes)
</script>

<template>
  <div class="admin-product-types-page">
    <div class="page-shell">
      <div class="top-bar">
        <Button
          label="Назад"
          icon="pi pi-arrow-left"
          class="back-btn"
          outlined
          @click="goBack"
        />

        <Button
          label="Добавить тип продукта"
          icon="pi pi-plus"
          class="create-btn"
          @click="openCreateProductType"
        />
      </div>

      <div class="page-header">
        <div>
          <h1>Типы продуктов</h1>
          <p>Управление типами продуктов и их структурой</p>
        </div>
      </div>

      <div class="filters">
  <div class="filter-group filter-group--search">
    <label>Поиск</label>
    <InputText
      v-model="search"
      placeholder="Поиск по названию типа"
      class="filter-control"
    />
  </div>

  <div class="filter-group">
    <label>Бренд</label>
    <Dropdown
      v-model="selectedBrandId"
      :options="brandOptions"
      optionLabel="label"
      optionValue="value"
      placeholder="Все бренды"
      class="filter-control"
    />
  </div>

  <div class="filter-group">
    <label>Категория</label>
    <Dropdown
      v-model="selectedCategoryId"
      :options="categoryOptions"
      optionLabel="label"
      optionValue="value"
      placeholder="Все категории"
      class="filter-control"
    />
  </div>
</div>

      <Card class="types-card">
        <template #content>
          <div v-if="loading" class="state-block">
            Загрузка...
          </div>

          <div v-else-if="productTypesList.length === 0" class="state-block">
            Типы продуктов не найдены
          </div>

          <div v-else class="product-types-grid">
            <div
              v-for="type in paginatedProductTypes"
              :key="type.id"
              class="product-type-card"
            >
              <div class="type-header">
                <div class="type-name">{{ type.name }}</div>
              </div>

              <div class="type-description">
                {{ type.description ?? 'Описание отсутствует' }}
              </div>

              <div class="type-meta">
                <span class="meta-chip">
                  {{ type.isActive ? 'Активен' : 'Неактивен' }}
                </span>

                <span class="meta-chip" v-if="type.attributes?.length">
                  Атрибутов: {{ type.attributes.length }}
                </span>
              </div>

              <div class="type-actions">
                <Button
                  icon="pi pi-eye"
                  class="icon-btn"
                  outlined
                  title="Просмотр"
                  @click="viewProductType(type)"
                />
                <Button
                  icon="pi pi-pencil"
                  class="icon-btn"
                  outlined
                  title="Редактировать"
                  @click="editProductType(type)"
                />
                <Button
                  icon="pi pi-trash"
                  class="icon-btn icon-btn--danger"
                  outlined
                  title="Удалить"
                  @click="deleteProductTypeConfirm(type)"
                />
              </div>
            </div>
          </div>
          <div v-if="totalPages > 1" class="pagination">
            <Button icon="pi pi-chevron-left" outlined :disabled="currentPage === 1" @click="currentPage--" />
            <span class="page-info">{{ currentPage }} / {{ totalPages }}</span>
            <Button icon="pi pi-chevron-right" outlined :disabled="currentPage === totalPages" @click="currentPage++" />
          </div>
        </template>
      </Card>

      <ProductTypeFormDialog
        :visible="productTypesDialogVisible"
        :mode="productTypeFormMode"
        :product-type="selectedProductType"
        :submitting="submitting"
        :field-errors="fieldErrors"
        @close="productTypesDialogVisible = false"
        @submit="handleSubmitProductType"
      />

      <DeleteProductTypeConfirmDialog
        :visible="deleteProductTypeDialogVisible"
        :product-type="selectedProductType"
        :deleting="deleting"
        @close="deleteProductTypeDialogVisible = false"
        @confirm="handleDeleteProductType"
      />
    </div>
  </div>
</template>

<style scoped>
.admin-product-types-page {
  background: #fafafa;
  min-height: 100vh;
}

.page-shell {
  max-width: 1240px;
  margin: 0 auto;
  padding: 32px 24px 48px;
}

.top-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-bottom: 24px;
}

.page-header {
  margin-bottom: 24px;
}

.page-header h1 {
  margin: 0;
  font-size: 36px;
  line-height: 1.1;
  letter-spacing: -0.03em;
  color: #111111;
}

.page-header p {
  margin: 10px 0 0;
  font-size: 15px;
  color: #6b6b6b;
}

.types-card {
  border: 1px solid #ececec;
  border-radius: 24px;
  box-shadow: 0 18px 40px rgba(17, 17, 17, 0.05);
  overflow: hidden;
}

:deep(.types-card.p-card) {
  background: #ffffff;
}

:deep(.types-card .p-card-content) {
  padding: 24px;
}

.state-block {
  padding: 28px;
  text-align: center;
  color: #777777;
  border: 1px dashed #d7d7d7;
  border-radius: 18px;
  background: #fafafa;
}

.product-types-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 18px;
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
  padding: 20px 0 8px;
}

.page-info {
  font-size: 14px;
  color: #555;
  min-width: 60px;
  text-align: center;
}

.product-type-card {
  min-width: 0;
  background: #ffffff;
  border: 1px solid #ececec;
  border-radius: 20px;
  padding: 18px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  box-shadow: 0 10px 24px rgba(17, 17, 17, 0.04);
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
}

.product-type-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 16px 34px rgba(17, 17, 17, 0.08);
  border-color: #dddddd;
}

.type-header {
  display: flex;
  align-items: center;
  gap: 10px;
}

.type-status {
  width: 9px;
  height: 9px;
  border-radius: 999px;
  background: #c41217;
  flex-shrink: 0;
}

.type-status--inactive {
  background: #b8b8b8;
}

.type-name {
  font-weight: 700;
  font-size: 18px;
  color: #111111;
  letter-spacing: -0.02em;
  word-break: break-word;
}

.type-description {
  overflow-wrap: anywhere;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
  overflow: hidden;
  height: 4.8em;
  flex: 0 0 auto;
  font-size: 14px;
  color: #6b6b6b;
  line-height: 1.6;
}

.type-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.meta-chip {
  display: inline-flex;
  align-items: center;
  padding: 6px 10px;
  border-radius: 999px;
  background: #f6f6f6;
  border: 1px solid #ececec;
  color: #555555;
  font-size: 12px;
  font-weight: 500;
}

.type-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: auto;
}

:deep(.back-btn.p-button) {
  background: #ffffff;
  border: 1px solid #d8d8d8;
  color: #111111;
  border-radius: 999px;
  box-shadow: none;
}

:deep(.back-btn.p-button:hover) {
  background: #f7f7f7;
  border-color: #111111;
  color: #111111;
}

:deep(.back-btn.p-button .p-button-icon) {
  color: #c41217;
}

:deep(.create-btn.p-button) {
  background: #111111;
  border: 1px solid #111111;
  color: #ffffff;
  border-radius: 999px;
  box-shadow: none;
}

:deep(.create-btn.p-button:hover) {
  background: #2a2a2a;
  border-color: #2a2a2a;
}

:deep(.icon-btn.p-button) {
  background: #ffffff;
  border: 1px solid #dddddd;
  color: #111111;
  border-radius: 999px;
  width: 42px;
  height: 42px;
  box-shadow: none;
}

:deep(.icon-btn.p-button:hover) {
  background: #f7f7f7;
  border-color: #111111;
  color: #111111;
}

:deep(.icon-btn.p-button .p-button-icon) {
  font-size: 14px;
}

:deep(.icon-btn--danger.p-button:hover) {
  border-color: #c41217;
  color: #c41217;
}

@media (max-width: 760px) {
  .page-shell {
    padding: 24px 16px 40px;
  }

  .top-bar {
    flex-direction: column;
    align-items: stretch;
  }

  .page-header h1 {
    font-size: 30px;
  }

  .product-types-grid {
    grid-template-columns: 1fr;
  }

  .filters {
    grid-template-columns: 1fr;
  }
}

.filters {
  display: grid;
  grid-template-columns: 2fr 1fr 1fr;
  gap: 16px;
  margin-bottom: 20px;
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.filter-group label {
  font-size: 13px;
  font-weight: 600;
  color: #444;
}

.filter-control {
  width: 100%;
}

:deep(.p-inputtext),
:deep(.p-dropdown) {
  border-radius: 14px;
  border-color: #ddd;
}

:deep(.p-inputtext:focus),
:deep(.p-dropdown.p-focus) {
  border-color: #111;
  box-shadow: 0 0 0 2px rgba(196,18,23,0.08);
}
</style>
